import {
  ACESFilmicToneMapping,
  HalfFloatType,
  PCFSoftShadowMap,
  SRGBColorSpace,
  Vector2,
  WebGLRenderTarget,
  WebGLRenderer,
} from 'three';
import type { PerspectiveCamera, Scene } from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';

export type Quality = 'high' | 'medium' | 'low';

export interface QualityProfile {
  pixelRatio: number;
  shadows: boolean;
  shadowMapSize: number;
  bloom: boolean;
  samples: number;
}

export function qualityProfile(quality: Quality): QualityProfile {
  const dpr = typeof window === 'undefined' ? 1 : window.devicePixelRatio || 1;
  switch (quality) {
    case 'high':
      return {
        pixelRatio: Math.min(dpr, 1.75),
        shadows: true,
        shadowMapSize: 2048,
        bloom: true,
        samples: 4,
      };
    case 'medium':
      return {
        pixelRatio: Math.min(dpr, 1.25),
        shadows: true,
        shadowMapSize: 1024,
        bloom: true,
        samples: 0,
      };
    default:
      return {
        pixelRatio: Math.min(dpr, 0.85),
        shadows: false,
        shadowMapSize: 512,
        bloom: false,
        samples: 0,
      };
  }
}

/** Final grade: gentle contrast, saturation lift, vignette and a touch of speed blur tint. */
const GradeShader = {
  uniforms: {
    tDiffuse: { value: null },
    vignette: { value: 0.32 },
    saturation: { value: 1.12 },
    contrast: { value: 1.06 },
    hurt: { value: 0 },
  },
  vertexShader: /* glsl */ `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: /* glsl */ `
    uniform sampler2D tDiffuse;
    uniform float vignette;
    uniform float saturation;
    uniform float contrast;
    uniform float hurt;
    varying vec2 vUv;
    void main() {
      vec4 color = texture2D(tDiffuse, vUv);
      float luma = dot(color.rgb, vec3(0.299, 0.587, 0.114));
      color.rgb = mix(vec3(luma), color.rgb, saturation);
      color.rgb = (color.rgb - 0.5) * contrast + 0.5;
      vec2 d = vUv - 0.5;
      float v = smoothstep(0.85, 0.2, length(d) * (1.0 + vignette));
      color.rgb *= mix(1.0, v, vignette * 1.6);
      float edge = smoothstep(0.35, 0.75, length(d));
      color.rgb = mix(color.rgb, vec3(0.75, 0.05, 0.08), hurt * edge * 0.6);
      gl_FragColor = color;
    }
  `,
};

export class Renderer {
  public readonly renderer: WebGLRenderer;
  private composer: EffectComposer | null = null;
  private bloom: UnrealBloomPass | null = null;
  private grade: ShaderPass | null = null;
  private profile: QualityProfile;

  public constructor(
    private readonly container: HTMLElement,
    private readonly scene: Scene,
    private readonly camera: PerspectiveCamera,
    quality: Quality,
  ) {
    this.profile = qualityProfile(quality);
    this.renderer = new WebGLRenderer({
      antialias: false,
      powerPreference: 'high-performance',
      stencil: false,
    });
    this.renderer.outputColorSpace = SRGBColorSpace;
    this.renderer.toneMapping = ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 0.9;
    this.renderer.shadowMap.type = PCFSoftShadowMap;
    this.renderer.domElement.className = 'game-canvas';
    container.appendChild(this.renderer.domElement);
    this.applyProfile();
    this.resize();
  }

  public get quality(): QualityProfile {
    return this.profile;
  }

  public setQuality(quality: Quality): QualityProfile {
    this.profile = qualityProfile(quality);
    this.applyProfile();
    this.resize();
    return this.profile;
  }

  private applyProfile(): void {
    const p = this.profile;
    this.renderer.setPixelRatio(p.pixelRatio);
    this.renderer.shadowMap.enabled = p.shadows;
    this.composer?.dispose();
    this.composer = null;
    this.bloom = null;
    this.grade = null;
    if (!p.bloom) {
      this.renderer.toneMapping = ACESFilmicToneMapping;
      return;
    }
    const size = this.renderer.getDrawingBufferSize(new Vector2());
    const target = new WebGLRenderTarget(Math.max(1, size.x), Math.max(1, size.y), {
      type: HalfFloatType,
      samples: p.samples,
    });
    this.composer = new EffectComposer(this.renderer, target);
    this.composer.addPass(new RenderPass(this.scene, this.camera));
    this.bloom = new UnrealBloomPass(new Vector2(size.x / 2, size.y / 2), 0.42, 0.5, 1.25);
    this.composer.addPass(this.bloom);
    this.composer.addPass(new OutputPass());
    this.grade = new ShaderPass(GradeShader);
    this.composer.addPass(this.grade);
  }

  public setHurt(amount: number): void {
    if (this.grade) this.grade.uniforms.hurt!.value = amount;
  }

  public resize(): void {
    const width = Math.max(1, this.container.clientWidth);
    const height = Math.max(1, this.container.clientHeight);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height, false);
    this.composer?.setPixelRatio(this.profile.pixelRatio);
    this.composer?.setSize(width, height);
  }

  public render(): void {
    if (this.composer) this.composer.render();
    else this.renderer.render(this.scene, this.camera);
  }

  public dispose(): void {
    this.composer?.dispose();
    this.renderer.dispose();
    this.renderer.domElement.remove();
  }
}
